/** Raw usecase item from static JSON (snake_case). */
export interface UsecaseJsonItem {
  id: string;
  title: string;
  datasets_required: string[];
  how_it_creates_value: string;
  primary_beneficiaries: string[];
  icon: string;
}

/** Static usecases data – cards built from this list. */
export const USECASES_JSON: UsecaseJsonItem[] = [
  {
    id: 'seed_variety_performance_tracking',
    title: 'Seed Variety Performance Tracking',
    datasets_required: ['Seed Registry', 'Digital Crop Survey', 'CropSAP', 'Soil Health Card', 'Weather Data'],
    how_it_creates_value: 'Aggregates real-world yield data across thousands of farms to rank seed varieties by performance in specific soil and climate conditions, replacing marketing claims with evidence.',
    primary_beneficiaries: ['Farmers', 'FPOs', 'Seed Companies', 'Agricultural Universities'],
    icon: 'plant'
  },
  {
    id: 'pest_outbreak_prediction',
    title: 'Pest Outbreak Prediction',
    datasets_required: ['Weather Forecasts', 'CropSAP Data', 'Pest Registry'],
    how_it_creates_value: 'Analyzes pest surveillance data, weather patterns, and crop stages to predict outbreaks days in advance, enabling timely preventive spraying instead of reactive damage control.',
    primary_beneficiaries: ['Farmers', 'FPOs', 'Extension Officers'],
    icon: 'bug'
  },
  {
    id: 'pest_disease_identification_control',
    title: 'Pest and Disease Identification & Control Measures',
    datasets_required: ['Pest & Disease Registry', 'Crop Registry', 'Pesticide Registry', 'SAU Research Database', 'Weather Data'],
    how_it_creates_value: 'Analyzes farmer-uploaded crop photos using AI image recognition to identify pest/disease within seconds, matches symptoms against diagnostic database, recommends treatment options based on economic thresholds and weather conditions, and provides bilingual advisories with nearby pesticide dealer locations and application instructions.',
    primary_beneficiaries: ['Farmers', 'Extension Officers', 'Pesticide Retailers'],
    icon: 'disease_control'
  },
  {
    id: 'custom_fertilizer_blending',
    title: 'Custom Fertilizer Blending',
    datasets_required: ['Seed Registry', 'Digital Crop Survey', 'CropSAP', 'Soil Health Card', 'Weather Data'],
    how_it_creates_value: 'Aggregates real-world yield data across thousands of farms to generate crop and soil-specific fertilizer blending recommendations for improved productivity.',
    primary_beneficiaries: ['Farmers', 'FPOs', 'Seed Companies', 'Agricultural Universities'],
    icon: 'fertilizer'
  },
  {
    id: 'maturity_based_harvest_optimization',
    title: 'Maturity-Based Harvest Optimization for Sugarcane and Horticultural Crops',
    datasets_required: [
      'Digital Crop Survey',
      'e-Pik Pahani',
      'Remote Sensing Data',
      'Weather Forecasts',
      'Crop Age/Planting Date Registry',
      'Field Observations (Brix levels for sugarcane, color/firmness for fruits)',
      'Historical Yield Data',
      'Market Price Intelligence',
      'Transportation/Cold Storage Availability'
    ],
    how_it_creates_value: 'Predicts optimal harvest windows by analyzing crop maturity indicators and market demand. Generates harvest schedules that maximize sugar recovery rates in sugarcane, minimize post-harvest losses in perishables, and align harvesting with peak market prices and logistics availability.',
    primary_beneficiaries: ['Farmers', 'FPOs', 'Sugar Mills', 'Food Processing Units', 'Cold Chain Operators', 'Market Aggregators'],
    icon: 'harvest'
  },
  {
    id: 'farm_gate_logistics_optimization',
    title: 'Farm Gate Logistics Optimization',
    datasets_required: ['Digital Crop Survey', 'GPS Network', 'APMC Prices', 'Weather Forecasts'],
    how_it_creates_value: 'Clusters farmers by location and harvest timing, generates optimal collection routes, and coordinates shared transport to reduce costs and post-harvest losses.',
    primary_beneficiaries: ['Farmers', 'FPOs', 'Cold Chain Operators'],
    icon: 'logistics'
  },
  {
    id: 'farm_machinery_custom_hiring',
    title: 'Farm Machinery Custom Hiring Service Availability',
    datasets_required: ['MahaDBT', 'GPS Locations', 'Weather Forecasts'],
    how_it_creates_value: 'Connects farmers needing equipment with nearby custom hiring centers and service providers, optimizes usage routes and availability, and makes mechanization affordable.',
    primary_beneficiaries: ['Farmers', 'FPOs providing Custom Hiring Center services'],
    icon: 'tractor'
  },
  {
    id: 'fodder_demand_supply_mitigation',
    title: 'Fodder Demand-Supply Shortage Mitigation',
    datasets_required: ['Livestock Census', 'Weather Forecasts', 'DCS'],
    how_it_creates_value: 'Forecasts fodder shortages in advance by analyzing drought predictions and livestock populations, triggers imports, and matches surplus regions with deficit areas to prevent price spikes and distress livestock sales.',
    primary_beneficiaries: ['Cattle Rearers', 'Dairy Industry and Cooperatives', 'Policy Makers', 'Drought Relief Programs'],
    icon: 'fodder'
  },
  {
    id: 'smart_crop_loan',
    title: 'Smart Crop Loan',
    datasets_required: ['Digital Crop Survey', 'Scale of Finance of Crops'],
    how_it_creates_value: 'Calculates farmer credit scores using yield history and potential income, enabling faster and data-driven loan approvals.',
    primary_beneficiaries: ['Farmers', 'FPOs', 'Banks', 'NBFCs'],
    icon: 'loan'
  },
  {
    id: 'farm_to_fork_traceability',
    title: 'Farm-to-Fork Traceability',
    datasets_required: ['Digital Crop Survey', 'Soil Health Card', 'Pesticide Records', 'APMC Transactions', 'Cold Chain IoT'],
    how_it_creates_value: 'Creates blockchain-verified supply chain records from farm to consumer, enabling instant recall during contamination events and commanding premium prices for traceable organic/export produce.',
    primary_beneficiaries: ['Farmers', 'FPOs', 'Exporters', 'Food Safety Authorities', 'Consumers'],
    icon: 'traceability'
  },
  {
    id: 'relief_and_rehabilitation',
    title: 'Relief and Rehabilitation',
    datasets_required: ['Weather Data', 'Satellite Imagery', 'Crop Spectral Signatures', 'Digital Crop Survey'],
    how_it_creates_value: 'Monitors weather triggers and verifies crop losses automatically using satellite data, enabling quicker relief measures and decision-making.',
    primary_beneficiaries: ['Farmers', 'Government'],
    icon: 'relief'
  }
];

/** Map JSON icon name to filename in public/usecase (without .png). */
export const USECASE_ICON_FILENAMES: Record<string, string> = {
  plant: 'seed',
  bug: 'pest',
  disease_control: 'diseaseIdentification',
  fertilizer: 'blending',
  fertiliser: 'blending',
  harvest: 'maturity',
  logistics: 'farmgate',
  tractor: 'farmmachinery',
  fodder: 'fodderdemand',
  loan: 'smartcroploan',
  traceability: 'fork',
  relief: 'releif'
};
